import React, { useState, useEffect, useRef } from "react";
import Button from "../../../components/ui/Button";
import Icon from "../../../components/AppIcon";
import { cn } from "../../../utils/cn";

const RecordControls = ({
  isRecording,
  recordResult,
  isMuted,
  micConnected,
  onToggleMute,
  onStartRecord,
  onStopRecord,
  onResetParams,
}) => {
  const [seconds, setSeconds] = useState(0);
  const timerRef = useRef(null);

  // Manage recording timer
  useEffect(() => {
    if (isRecording) {
      setSeconds(0);
      timerRef.current = setInterval(() => {
        setSeconds((prev) => prev + 1);
      }, 1000);
    } else {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    }

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [isRecording]);

  // Format seconds to MM:SS
  const formatTime = (totalSeconds) => {
    const minutes = Math.floor(totalSeconds / 60);
    const secs = totalSeconds % 60;
    return `${minutes.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  };

  return (
    <div className="bg-card border border-border rounded-lg p-5 font-fira terminal-glow flex flex-col items-center justify-center gap-4 text-center">
      {/* Tape Deck Module / Status */}
      <div className="flex flex-row items-center justify-center space-x-3">
        <div className="relative w-10 h-7 bg-black/60 rounded border border-border flex items-center justify-center overflow-hidden flex-shrink-0">
          {/* Cassette Tape Spools Animation */}
          {isRecording ? (
            <div className="flex space-x-1.5">
              <div className="w-3.5 h-3.5 rounded-full border border-dashed border-primary animate-spin" style={{ animationDuration: "3s" }} />
              <div className="w-3.5 h-3.5 rounded-full border border-dashed border-primary animate-spin" style={{ animationDuration: "3s" }} />
            </div>
          ) : (
            <div className="flex space-x-1.5 opacity-30">
              <div className="w-3.5 h-3.5 rounded-full border border-dashed border-muted-foreground" />
              <div className="w-3.5 h-3.5 rounded-full border border-dashed border-muted-foreground" />
            </div>
          )}
        </div>

        <div className="text-left">
          <div className="flex items-center space-x-1.5">
            <h3 className="text-[11px] font-bold text-foreground uppercase tracking-wider">
              Output Recorder
            </h3>
            {isRecording && (
              <span className="flex h-1.5 w-1.5 relative">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-destructive opacity-75"></span>
                <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-destructive"></span>
              </span>
            )}
          </div>
          <p className="text-[9px] text-muted-foreground font-source leading-tight">
            {isRecording
              ? `RECORDING ACTIVE - ${formatTime(seconds)}`
              : recordResult
              ? "TRACK COMPILED - READY TO DOWNLOAD"
              : "READY - TAPE LOADED"}
          </p>
        </div>
      </div>

      {/* Action Buttons: Centered in one single line row */}
      <div className="flex flex-row flex-wrap items-center justify-center gap-2.5 w-full">
        {/* Mute Button */}
        <Button
          variant={isMuted ? "danger" : "outline"}
          size="sm"
          disabled={!micConnected}
          onClick={onToggleMute}
          iconName={isMuted ? "VolumeX" : "Volume2"}
          className="font-bold text-xs uppercase h-8 px-2.5"
        >
          {isMuted ? "MUTED" : "MUTE"}
        </Button>

        {/* Record trigger buttons */}
        {!isRecording ? (
          <Button
            variant="outline"
            size="sm"
            disabled={!micConnected}
            onClick={onStartRecord}
            iconName="Circle"
            className="font-bold text-xs text-destructive hover:text-destructive hover:bg-destructive/10 border-destructive/30 uppercase h-8 px-2.5"
          >
            Record
          </Button>
        ) : (
          <Button
            variant="destructive"
            size="sm"
            onClick={onStopRecord}
            iconName="Square"
            className="font-bold text-xs uppercase h-8 px-2.5"
          >
            Stop
          </Button>
        )}

        {/* Download Link */}
        {recordResult && (
          <a
            href={recordResult.url}
            download={`developernoob_echo_studio_${Date.now()}.${recordResult.format || 'webm'}`}
            className="inline-flex items-center justify-center whitespace-nowrap rounded-md text-xs font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 h-8 px-2.5 bg-success text-success-foreground hover:bg-success/90 font-bold uppercase"
          >
            <Icon name="Download" size={12} className="mr-1.5" />
            Download
          </a>
        )}

        {/* Reset Console */}
        <Button
          variant="ghost"
          size="sm"
          onClick={onResetParams}
          iconName="RotateCcw"
          className="font-bold text-xs text-muted-foreground hover:text-foreground uppercase h-8 px-2.5"
        >
          Reset
        </Button>
      </div>
    </div>
  );
};

export default RecordControls;
